import React, { useState } from 'react';
import css from './Sections.module.css';
import { Icons, NamedLink } from '../../../components';
import { REVIEW_TYPE_OF_CUSTOMER, REVIEW_TYPE_OF_PROVIDER } from '../../../util/types';
import noImage from '../../../assets/no-image.jpg';
import { MY_LISTINGS_TAB_TYPES } from '../../../util/enums';
import Reviews from './Reviews';
import { createSlug } from '../../../util/urlHelpers';

const REPORTED_VESSEL_CHECK_IN_TYPE = 'reported_vessel';

const formatDateTime = (intl, value) => {
  if (!value) return null;
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return `${intl.formatDate(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}, ${intl.formatTime(date, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

const getCheckInTypeLabel = (intl, checkIn) =>
  intl.formatMessage({
    id:
      checkIn?.type === REPORTED_VESSEL_CHECK_IN_TYPE
        ? 'ProfilePage.SectionMyListings.reportedCheckIn'
        : 'ProfilePage.SectionMyListings.ownCheckIn',
  });

const getCheckInStatusLabel = (intl, status) => {
  const normalizedStatus =
    status === 'expired' ? 'expired' : status === 'active' ? 'active' : 'closed';

  return intl.formatMessage({
    id: `ProfilePage.SectionMyListings.${normalizedStatus}Status`,
  });
};

const getVesselLabel = (intl, checkIn) => {
  if (checkIn?.type === REPORTED_VESSEL_CHECK_IN_TYPE) {
    return (
      checkIn?.reportedVesselName ||
      intl.formatMessage({ id: 'ProfilePage.SectionMyListings.anotherVessel' })
    );
  }

  return (
    checkIn?.vessel?.name ||
    checkIn?.vessel?.boatName ||
    intl.formatMessage({ id: 'ProfilePage.SectionMyListings.unknownVessel' })
  );
};

const SectionMyListings = props => {
  const {
    listings = [],
    reviews = [],
    checkIns = [],
    checkInsLoading,
    checkInsError,
    intl,
  } = props;
  const [activeTab, setActiveTab] = useState(MY_LISTINGS_TAB_TYPES?.LISTINGS);
  const [showAllListings, setShowAllListings] = useState(false);

  const reviewsOfProvider = reviews?.filter(r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r?.attributes?.type === REVIEW_TYPE_OF_CUSTOMER);

  const listingReviewsMap = {};
  reviews.forEach(review => {
    if (review.attributes.type === REVIEW_TYPE_OF_PROVIDER && review.listing) {
      const listingId = review.listing.id.uuid;
      if (!listingReviewsMap[listingId]) {
        listingReviewsMap[listingId] = { sum: 0, count: 0 };
      }
      listingReviewsMap[listingId].sum += review.attributes.rating;
      listingReviewsMap[listingId].count += 1;
    }
  });

  const listingsToShow = showAllListings ? listings : listings?.slice(0, 2) || [];

  return (
    <div className={css.myListingsSection}>
      <div className={css.tabBar}>
        <button
          type="button"
          className={`${css.tab} ${
            activeTab === MY_LISTINGS_TAB_TYPES?.LISTINGS ? css.activeTab : ''
          }`}
          onClick={() => setActiveTab(MY_LISTINGS_TAB_TYPES?.LISTINGS)}
        >
          {intl.formatMessage(
            { id: 'ProfilePage.SectionMyListings.myListingsTab' },
            { count: listings?.length }
          )}
        </button>
        <button
          type="button"
          className={`${css.tab} ${
            activeTab === MY_LISTINGS_TAB_TYPES?.REVIEW_RECEIVED ? css.activeTab : ''
          }`}
          onClick={() => setActiveTab(MY_LISTINGS_TAB_TYPES?.REVIEW_RECEIVED)}
        >
          {intl.formatMessage(
            { id: 'ProfilePage.SectionMyListings.reviewsReceivedTab' },
            { count: reviewsOfProvider?.length }
          )}
        </button>
        <button
          type="button"
          className={`${css.tab} ${
            activeTab === MY_LISTINGS_TAB_TYPES?.REVIEW_GIVEN ? css.activeTab : ''
          }`}
          onClick={() => setActiveTab(MY_LISTINGS_TAB_TYPES?.REVIEW_GIVEN)}
        >
          {intl.formatMessage(
            { id: 'ProfilePage.SectionMyListings.reviewsGivenTab' },
            { count: reviewsOfCustomer?.length }
          )}
        </button>
        <button
          type="button"
          className={`${css.tab} ${
            activeTab === MY_LISTINGS_TAB_TYPES?.CHECK_INS ? css.activeTab : ''
          }`}
          onClick={() => setActiveTab(MY_LISTINGS_TAB_TYPES?.CHECK_INS)}
        >
          {intl.formatMessage(
            { id: 'ProfilePage.SectionMyListings.checkInsTab' },
            { count: checkIns?.length }
          )}
        </button>
      </div>

      {activeTab === MY_LISTINGS_TAB_TYPES?.LISTINGS && (
        <div className={css.listingsContainer}>
          {listingsToShow?.map(listing => {
            const stats = listingReviewsMap[listing?.id?.uuid] || { sum: 0, count: 0 };
            const avgRating = stats.count > 0 ? (stats.sum / stats.count).toFixed(1) : 0;

            return (
              <div key={listing?.id?.uuid} className={css.listingItem}>
                <div className={css.listingImage}>
                  <img
                    src={
                      listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url || noImage
                    }
                    alt={listing?.attributes?.title}
                  />
                </div>
                <div className={css.listingContent}>
                  <div className={css.listingContentStart}>
                    <h4 className={css.listingTitle}>{listing?.attributes?.title}</h4>
                    <p className={css.listingDescription}>{listing?.attributes?.description}</p>
                    <div className={css.rating}>
                      <Icons name="starFilled" />
                      {intl.formatMessage(
                        { id: 'ProfilePage.SectionMyListings.rating' },
                        {
                          rating: avgRating,
                          reviewCount: stats.count,
                        }
                      )}
                    </div>
                  </div>
                  <span className={css.price}>
                    ${listing?.attributes?.price?.amount / 100}/
                    {listing?.attributes?.publicData?.unitType}
                  </span>{' '}
                </div>
              </div>
            );
          })}
          {listings?.length > 2 && !showAllListings && (
            <span
              type="button"
              className={css.listingShowAllButton}
              onClick={() => setShowAllListings(true)}
            >
              {intl.formatMessage({ id: 'ProfilePage.SectionMyListings.viewAllListings' })}
            </span>
          )}
        </div>
      )}

      {activeTab === MY_LISTINGS_TAB_TYPES?.REVIEW_RECEIVED && (
        <div className={css.tabContent}>
          <Reviews reviews={reviewsOfProvider} intl={intl} />
        </div>
      )}

      {activeTab === MY_LISTINGS_TAB_TYPES?.REVIEW_GIVEN && (
        <div className={css.tabContent}>
          <Reviews reviews={reviewsOfCustomer} intl={intl} />
        </div>
      )}

      {activeTab === MY_LISTINGS_TAB_TYPES?.CHECK_INS && (
        <div className={css.checkInsContainer}>
          {checkInsLoading ? (
            <p className={css.checkInsEmpty}>
              {intl.formatMessage({ id: 'ProfilePage.SectionMyListings.checkInsLoading' })}
            </p>
          ) : checkInsError ? (
            <p className={css.checkInsEmpty}>
              {intl.formatMessage({ id: 'ProfilePage.SectionMyListings.checkInsError' })}
            </p>
          ) : checkIns.length === 0 ? (
            <p className={css.checkInsEmpty}>
              {intl.formatMessage({ id: 'ProfilePage.SectionMyListings.noCheckIns' })}
            </p>
          ) : (
            checkIns.map(checkIn => {
              const arrivedAt = formatDateTime(intl, checkIn?.arrivalTime || checkIn?.createdAt);
              const departedAt = formatDateTime(intl, checkIn?.departureTime);
              const expiresAt = formatDateTime(intl, checkIn?.expiresAt);
              const isActive = checkIn?.status === 'active';
              const secondaryDate = isActive && expiresAt ? expiresAt : departedAt;
              const listingTitle =
                checkIn?.listingTitle ||
                intl.formatMessage({ id: 'ProfilePage.SectionMyListings.publicMoorage' });
              const listingSlug = createSlug(listingTitle);

              return (
                <div key={checkIn?.id} className={css.checkInItem}>
                  <div className={css.checkInImage}>
                    {checkIn?.listingImage ? (
                      <img src={checkIn.listingImage} alt={listingTitle} />
                    ) : (
                      <span>OB</span>
                    )}
                  </div>
                  <div className={css.checkInContent}>
                    <div className={css.checkInHeader}>
                      <h4 className={css.checkInTitle}>
                        {checkIn?.listingId ? (
                          <NamedLink
                            className={css.checkInTitleLink}
                            name="ListingPage"
                            params={{ id: checkIn.listingId, slug: listingSlug }}
                          >
                            {listingTitle}
                          </NamedLink>
                        ) : (
                          listingTitle
                        )}
                      </h4>
                      <span
                        className={`${css.checkInStatus} ${
                          isActive ? css.activeCheckInStatus : ''
                        }`}
                      >
                        {getCheckInStatusLabel(intl, checkIn?.status)}
                      </span>
                    </div>
                    <p className={css.checkInDescription}>
                      {getCheckInTypeLabel(intl, checkIn)} - {getVesselLabel(intl, checkIn)}
                    </p>
                    <div className={css.checkInMeta}>
                      {arrivedAt ? (
                        <span>
                          {intl.formatMessage(
                            { id: 'ProfilePage.SectionMyListings.arrivedAt' },
                            { date: arrivedAt }
                          )}
                        </span>
                      ) : null}
                      {secondaryDate ? (
                        <span>
                          {intl.formatMessage(
                            {
                              id: isActive
                                ? 'ProfilePage.SectionMyListings.activeUntil'
                                : 'ProfilePage.SectionMyListings.departedAt',
                            },
                            { date: secondaryDate }
                          )}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SectionMyListings;
